import React, { useState } from 'react';

import { ListerItem, Link, Card, Button, type DataItemProps, AccordionRow } from '@ergeon/core-components';
import { ArrowTopRightOnSquareIcon, ClockIcon } from '@ergeon/icons/hero/micro';

import { CustomerDetail } from '../../../types';
import { useCustomerLocalTime } from '../../../hooks/useCustomerLocalTime';
import { getAccordionExpanded, setAccordionExpanded } from '../../../utils/appStorage';

import { renderEmailList } from './EmailList';
import { PhoneButtons } from './PhoneButtons';

type CustomerInfoProps = {
  customerDetail: CustomerDetail | null;
};

const ACCORDION_KEY = 'customerInfo';

const CustomerInfo = ({ customerDetail }: CustomerInfoProps) => {
  const localTimeInfo = useCustomerLocalTime(customerDetail?.address || null);
  const [expanded, setExpanded] = useState(() => getAccordionExpanded(ACCORDION_KEY, true));

  const handleExpandChange = (isExpanded: boolean) => {
    setExpanded(isExpanded);
    setAccordionExpanded(ACCORDION_KEY, isExpanded);
  };

  if (!customerDetail) return null;

  const emailsValue = renderEmailList(customerDetail.primary_email, customerDetail.additional_emails);

  const items: DataItemProps[] = [
    {
      title: 'Phone Number',
      value: (
        <PhoneButtons
          additionalPhones={customerDetail.additional_phones}
          phoneNumber={customerDetail.phone_number}
          primaryPhone={customerDetail.primary_phone} />
      ),
    },
    {
      title: 'Email',
      value: emailsValue,
    },
    {
      title: 'Address',
      value: customerDetail.address?.formatted_address && (
        <Link
          className="flex items-center gap-1"
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(customerDetail.address.formatted_address)}`}
          rel="noreferrer"
          target="_blank">
          {customerDetail.address.formatted_address}
          <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
        </Link>
      ),
    },
    {
      title: 'Local Time',
      value: localTimeInfo.time && (
        <div className="flex items-center gap-1">
          <ClockIcon className="size-4 text-gray-400 dark:text-gray-700" />
          {localTimeInfo.time} {localTimeInfo.abbreviation && `(${localTimeInfo.abbreviation})`}
        </div>
      ),
    },
    {
      title: 'Last Order',
      value: customerDetail.last_order && (
        <Link
          className="flex items-center gap-1"
          href={customerDetail.last_order.public_admin_url}
          rel="noreferrer"
          target="_blank">
          #{customerDetail.last_order.id}
          <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
        </Link>
      ),
    },
    {
      title: 'Last Quote',
      value: customerDetail.last_quote && (
        <Link
          className="flex items-center gap-1"
          href={customerDetail.last_quote.public_admin_url}
          rel="noreferrer"
          target="_blank">
          #{customerDetail.last_quote.id}
          <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
        </Link>
      ),
    },
    {
      title: 'Appointment Summary',
      value: customerDetail.last_order && (
        <Link
          className="flex items-center gap-1"
          href={`https://api.ergeon.in/public-admin/store/orderappointment/${customerDetail.last_order.id}/change/`}
          rel="noreferrer"
          target="_blank">
          Order #{customerDetail.last_order.id}
          <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
        </Link>
      ),
    },
  ];

  const buttons = [
    <Button
      aria-label="Edit customer info in Public Admin"
      asAnchor
      className="whitespace-nowrap"
      flavor="regular"
      href={customerDetail.public_admin_url}
      key="public-admin-button"
      size="small"
      target="_blank"
      taste="line">
      <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
      Edit Customer Info
    </Button>,
  ];

  return (
    <Card className="relative flex flex-col gap-3 px-4 py-0 w-full" flavor="base" zIndex="1">
      <AccordionRow
        expanded={expanded}
        hasBorder={false}
        isFullWidth
        setExpanded={handleExpandChange}
        title="Customer Info">
        <ListerItem
          aria-label="Customer information details"
          buttons={buttons}
          items={items}
          title={customerDetail.full_name} />
      </AccordionRow>
    </Card>
  );
};

export default CustomerInfo;
